import type { TempoEvent, TempoRampShape, TimeSignatureEvent } from '@/domain/types';

/**
 * Timing core.
 *
 * Every conversion in the DAW goes through this module, so that the timeline,
 * the scheduler, the piano roll and the offline renderer all agree on where a
 * musical position sits in seconds — and vice versa.
 *
 * The tempo map is a piecewise function of tick position. A segment is either
 * constant — BPM held until the next event — or a ramp from this event's BPM
 * to the next one's, which is how an accelerando or ritardando is written. A
 * ramp is linear in BPM or exponential in it: linear adds the same BPM every
 * beat, exponential multiplies by the same ratio every beat, which is the
 * shape a ritardando that *sounds* even has.
 *
 * All three are closed-form in both directions, so there is no iterative
 * search and no accumulated drift:
 *
 *   constant     seconds = ticks / ppq * 60 / bpm
 *   linear       with bpm(x) = b0 + kx over a segment of length L, k = (b1-b0)/L,
 *                seconds(x) = 60/(ppq·k) · ln(1 + kx/b0)
 *                ticks(T)   = b0/k · (exp(T·ppq·k/60) − 1)
 *   exponential  with bpm(x) = b0 · e^(rx), r = ln(b1/b0)/L,
 *                seconds(x) = 60/(ppq·b0·r) · (1 − e^(−rx))
 *                ticks(T)   = −ln(1 − T·ppq·b0·r/60) / r
 *
 * The ramp integral is of 1/bpm, not of bpm: it takes longer to play a beat at
 * a slower tempo, and averaging the BPM endpoints instead — the obvious wrong
 * answer — puts every later note in the wrong place.
 */

export interface NormalisedTempoMap {
  /** Sorted, deduplicated tempo events; always starts at tick 0. */
  events: TempoEvent[];
  /** Cumulative seconds at the start of each event's segment. */
  secondsAt: number[];
  ppq: number;
}

/**
 * Below this difference in BPM a ramp is treated as constant. A ramp of
 * 1e-9 BPM is inaudible, but its `k` divides into the logarithm, so leaving it
 * in the ramp branch would trade an exact answer for a numerically ugly one.
 */
const RAMP_EPSILON_BPM = 1e-9;

/** True when segment `i` ramps: it is marked, and there is a next event to ramp to. */
function isRamp(events: readonly TempoEvent[], i: number): boolean {
  if (!events[i].ramp || i + 1 >= events.length) return false;
  return Math.abs(events[i + 1].bpm - events[i].bpm) > RAMP_EPSILON_BPM;
}

/** The exponent per tick of an exponential ramp over segment `i`. */
function rampRate(events: readonly TempoEvent[], i: number): number {
  const length = events[i + 1].tick - events[i].tick;
  return Math.log(events[i + 1].bpm / events[i].bpm) / length;
}

/** Seconds elapsed from the start of segment `i` to `tick` inside it. */
function secondsIntoSegment(map: NormalisedTempoMap, i: number, tick: number): number {
  const { events, ppq } = map;
  const e = events[i];
  const x = tick - e.tick;
  if (!isRamp(events, i)) return (x / ppq) * (60 / e.bpm);
  if (e.rampShape === 'exponential') {
    const r = rampRate(events, i);
    return (60 / (ppq * e.bpm * r)) * (1 - Math.exp(-r * x));
  }
  const length = events[i + 1].tick - e.tick;
  const k = (events[i + 1].bpm - e.bpm) / length;
  return (60 / (ppq * k)) * Math.log(1 + (k * x) / e.bpm);
}

/**
 * A ramping segment as plain numbers: enough to walk it without the map.
 *
 * The warp renderer runs in a worker and follows a ramp continuously, so it
 * needs the ramp as data rather than as a closure over the tempo map.
 */
export interface TempoRampShapeData {
  b0: number;
  b1: number;
  lengthTicks: number;
  shape: TempoRampShape;
}

/** The ramping segment governing tick `tick`, as data, or null where the tempo holds. */
export function rampAt(map: NormalisedTempoMap, tick: number): TempoRampShapeData | null {
  const i = segmentIndexForTick(map, Math.max(0, tick));
  if (!isRamp(map.events, i)) return null;
  const e = map.events[i];
  const next = map.events[i + 1];
  return { b0: e.bpm, b1: next.bpm, lengthTicks: next.tick - e.tick, shape: e.rampShape ?? 'linear' };
}

/** Seconds from a segment's start event to `tick`, for a tick inside it. */
export function secondsIntoSegmentAt(map: NormalisedTempoMap, tick: number): number {
  const i = segmentIndexForTick(map, Math.max(0, tick));
  return secondsIntoSegment(map, i, Math.max(0, tick));
}

/** Ticks advanced from a ramp's start after `seconds`, in closed form for either shape. */
export function rampTicksInto(ramp: TempoRampShapeData, ppq: number, seconds: number): number {
  if (ramp.shape === 'exponential') {
    const r = Math.log(ramp.b1 / ramp.b0) / ramp.lengthTicks;
    // The segment's own end is the most this can be asked for; the clamp keeps
    // a rounding error at the boundary from reaching the logarithm's pole.
    return -Math.log(Math.max(1e-12, 1 - (seconds * ppq * ramp.b0 * r) / 60)) / r;
  }
  const k = (ramp.b1 - ramp.b0) / ramp.lengthTicks;
  return (ramp.b0 / k) * (Math.exp((seconds * ppq * k) / 60) - 1);
}

/** Ticks into segment `i` after `seconds` have elapsed from its start. */
function ticksIntoSegment(map: NormalisedTempoMap, i: number, seconds: number): number {
  const { events, ppq } = map;
  const e = events[i];
  if (!isRamp(events, i)) return (seconds * e.bpm * ppq) / 60;
  const next = events[i + 1];
  return rampTicksInto({ b0: e.bpm, b1: next.bpm, lengthTicks: next.tick - e.tick, shape: e.rampShape ?? 'linear' }, ppq, seconds);
}

const MIN_BPM = 5;
const MAX_BPM = 900;

export function normaliseTempoMap(events: readonly TempoEvent[], ppq: number): NormalisedTempoMap {
  const sorted = [...events]
    .filter((e) => Number.isFinite(e.tick) && Number.isFinite(e.bpm))
    // `ramp` is only written when true, so a project with no tempo shape
    // serialises exactly as it did before ramps existed.
    .map((e) => ({
      tick: Math.max(0, Math.round(e.tick)),
      bpm: Math.min(MAX_BPM, Math.max(MIN_BPM, e.bpm)),
      ...(e.ramp === true ? { ramp: true as const } : {}),
      ...(e.ramp === true && e.rampShape === 'exponential' ? { rampShape: 'exponential' as const } : {}),
    }))
    .sort((a, b) => a.tick - b.tick);

  if (sorted.length === 0 || sorted[0].tick !== 0) {
    sorted.unshift({ tick: 0, bpm: sorted.length > 0 ? sorted[0].bpm : 120 });
  }

  // Collapse events that share a tick — the last one wins. Events that merely
  // repeat the tempo before them are kept: a marker at the same BPM is how a
  // ramp is anchored, and how the editor inserts a change before it is edited.
  // Dropping them would make the map disagree with what the user placed.
  const events2: TempoEvent[] = [];
  for (const e of sorted) {
    const prev = events2[events2.length - 1];
    if (prev && prev.tick === e.tick) events2[events2.length - 1] = e;
    else events2.push(e);
  }

  const map: NormalisedTempoMap = { events: events2, secondsAt: new Array(events2.length), ppq };
  map.secondsAt[0] = 0;
  for (let i = 1; i < events2.length; i++) {
    map.secondsAt[i] = map.secondsAt[i - 1] + secondsIntoSegment(map, i - 1, events2[i].tick);
  }
  return map;
}

function segmentIndexForTick(map: NormalisedTempoMap, tick: number): number {
  const { events } = map;
  let lo = 0;
  let hi = events.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (events[mid].tick <= tick) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

function segmentIndexForSeconds(map: NormalisedTempoMap, seconds: number): number {
  const { secondsAt } = map;
  let lo = 0;
  let hi = secondsAt.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (secondsAt[mid] <= seconds) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/** Musical position (ticks) -> wall-clock seconds from project start. */
export function ticksToSeconds(map: NormalisedTempoMap, tick: number): number {
  const t = Math.max(0, tick);
  const i = segmentIndexForTick(map, t);
  return map.secondsAt[i] + secondsIntoSegment(map, i, t);
}

/** Wall-clock seconds from project start -> musical position (ticks). */
export function secondsToTicks(map: NormalisedTempoMap, seconds: number): number {
  const s = Math.max(0, seconds);
  const i = segmentIndexForSeconds(map, s);
  return map.events[i].tick + ticksIntoSegment(map, i, s - map.secondsAt[i]);
}

/** Duration in seconds of a span that starts at `tick` and lasts `lengthTicks`. */
export function tickSpanToSeconds(map: NormalisedTempoMap, tick: number, lengthTicks: number): number {
  return ticksToSeconds(map, tick + lengthTicks) - ticksToSeconds(map, tick);
}

/** Length in ticks of a span that starts at `tick` and lasts `seconds`. */
export function secondsSpanToTicks(map: NormalisedTempoMap, tick: number, seconds: number): number {
  const startSec = ticksToSeconds(map, tick);
  return secondsToTicks(map, startSec + seconds) - tick;
}

/** BPM in effect at a given tick — interpolated when the segment ramps. */
export function tempoAt(map: NormalisedTempoMap, tick: number): number {
  const t = Math.max(0, tick);
  const i = segmentIndexForTick(map, t);
  const e = map.events[i];
  if (!isRamp(map.events, i)) return e.bpm;
  const next = map.events[i + 1];
  const progress = Math.min(1, Math.max(0, (t - e.tick) / (next.tick - e.tick)));
  if (e.rampShape === 'exponential') return e.bpm * Math.pow(next.bpm / e.bpm, progress);
  return e.bpm + (next.bpm - e.bpm) * progress;
}

/**
 * The tempo event governing a position, and whether its segment is *marked* as
 * ramping.
 *
 * `ramps` here is the stored intent, not the audible effect: a ramp between two
 * equal tempos is flat, but the editor still has to show its toggle as on, or
 * turning it on before typing the destination tempo would look like a dead
 * control. The maths uses `isRamp`, which additionally requires a real slope.
 */
export function tempoEventAt(map: NormalisedTempoMap, tick: number): { event: TempoEvent; ramps: boolean } {
  const i = segmentIndexForTick(map, Math.max(0, tick));
  return { event: map.events[i], ramps: map.events[i].ramp === true && i + 1 < map.events.length };
}

// ---------------------------------------------------------------------------
// Time signatures / bars and beats
// ---------------------------------------------------------------------------

export interface NormalisedSignatureMap {
  events: TimeSignatureEvent[];
  /** Bar number (0-based) at the start of each signature segment. */
  barAt: number[];
  ppq: number;
}

export function ticksPerBar(sig: TimeSignatureEvent, ppq: number): number {
  return Math.round(sig.numerator * (4 / sig.denominator) * ppq);
}

export function ticksPerBeat(sig: TimeSignatureEvent, ppq: number): number {
  return Math.round((4 / sig.denominator) * ppq);
}

export function normaliseSignatureMap(
  events: readonly TimeSignatureEvent[],
  ppq: number,
): NormalisedSignatureMap {
  const sorted = [...events]
    .filter((e) => e.numerator > 0 && e.denominator > 0)
    .map((e) => ({
      tick: Math.max(0, Math.round(e.tick)),
      numerator: Math.round(e.numerator),
      denominator: Math.round(e.denominator),
    }))
    .sort((a, b) => a.tick - b.tick);

  if (sorted.length === 0 || sorted[0].tick !== 0) {
    sorted.unshift({ tick: 0, numerator: 4, denominator: 4 });
  }

  const out: TimeSignatureEvent[] = [];
  for (const e of sorted) {
    const prev = out[out.length - 1];
    if (prev && prev.tick === e.tick) out[out.length - 1] = e;
    else out.push(e);
  }

  // A meter change always starts a bar. If one lands part-way through a bar the
  // bar it interrupts is simply short, and still counts — rounding up here is
  // what keeps ticksToBarBeat and barBeatToTicks inverses of each other. With
  // changes on bar lines, which is all the command layer will write, ceil and
  // floor agree.
  const barAt: number[] = new Array(out.length);
  barAt[0] = 0;
  for (let i = 1; i < out.length; i++) {
    const span = out[i].tick - out[i - 1].tick;
    barAt[i] = barAt[i - 1] + Math.ceil(span / ticksPerBar(out[i - 1], ppq));
  }
  return { events: out, barAt, ppq };
}

export interface SignatureSegment {
  sig: TimeSignatureEvent;
  /** First tick governed by this signature. */
  startTick: number;
  /** First tick of the next signature, or Infinity for the last segment. */
  endTick: number;
  /** 0-based bar number at `startTick`. */
  startBar: number;
}

/**
 * The signature segments overlapping a tick range, in order.
 *
 * Anything that draws or walks a grid over a span — the ruler, the piano roll,
 * the metronome — has to do it segment by segment, because the bar and beat
 * lengths change at each event. Taking the signature at the start of the span
 * and using it throughout is the bug this exists to prevent.
 */
export function signatureSegments(
  map: NormalisedSignatureMap,
  fromTick: number,
  toTick: number,
): SignatureSegment[] {
  const from = Math.max(0, fromTick);
  if (!(toTick > from)) return [];

  const out: SignatureSegment[] = [];
  for (let i = signatureIndexForTick(map, from); i < map.events.length; i++) {
    const startTick = map.events[i].tick;
    if (startTick >= toTick) break;
    const endTick = i + 1 < map.events.length ? map.events[i + 1].tick : Number.POSITIVE_INFINITY;
    out.push({ sig: map.events[i], startTick, endTick, startBar: map.barAt[i] });
  }
  return out;
}

export interface BeatPosition {
  tick: number;
  /** True on the first beat of a bar, including the first beat of a segment. */
  downbeat: boolean;
}

/**
 * Every beat in `[fromTick, toTick)`, with the downbeats marked.
 *
 * Used by the metronome. Kept here, as a pure function over the map, so that
 * what the click plays can be asserted in a test without an audio context.
 */
export function beatGrid(
  map: NormalisedSignatureMap,
  fromTick: number,
  toTick: number,
  limit = 4096,
): BeatPosition[] {
  const out: BeatPosition[] = [];
  for (const segment of signatureSegments(map, fromTick, toTick)) {
    const beat = ticksPerBeat(segment.sig, map.ppq);
    const bar = ticksPerBar(segment.sig, map.ppq);
    const end = Math.min(segment.endTick, toTick);
    // Start on the first beat line at or after the window, measured from the
    // segment's own origin rather than from tick zero.
    const first = Math.max(0, Math.ceil((Math.max(fromTick, segment.startTick) - segment.startTick) / beat));
    for (let n = first; ; n++) {
      const tick = segment.startTick + n * beat;
      if (tick >= end) break;
      out.push({ tick, downbeat: (tick - segment.startTick) % bar === 0 });
      if (out.length >= limit) return out;
    }
  }
  return out;
}

function signatureIndexForTick(map: NormalisedSignatureMap, tick: number): number {
  let lo = 0;
  let hi = map.events.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (map.events[mid].tick <= tick) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

export function signatureAt(map: NormalisedSignatureMap, tick: number): TimeSignatureEvent {
  return map.events[signatureIndexForTick(map, Math.max(0, tick))];
}

export interface BarBeatTick {
  /** 1-based bar number, as shown in the transport. */
  bar: number;
  /** 1-based beat within the bar. */
  beat: number;
  /** Ticks past the beat. */
  ticks: number;
}

export function ticksToBarBeat(map: NormalisedSignatureMap, tick: number): BarBeatTick {
  const t = Math.max(0, Math.floor(tick));
  const i = signatureIndexForTick(map, t);
  const sig = map.events[i];
  const tpb = ticksPerBeat(sig, map.ppq);
  const tpBar = ticksPerBar(sig, map.ppq);
  const rel = t - sig.tick;
  const bar = map.barAt[i] + Math.floor(rel / tpBar);
  const inBar = rel - Math.floor(rel / tpBar) * tpBar;
  return { bar: bar + 1, beat: Math.floor(inBar / tpb) + 1, ticks: inBar % tpb };
}

export function barBeatToTicks(map: NormalisedSignatureMap, bar: number, beat = 1, ticks = 0): number {
  const targetBar = Math.max(0, Math.floor(bar) - 1);
  let i = 0;
  for (let k = 0; k < map.events.length; k++) {
    if (map.barAt[k] <= targetBar) i = k;
    else break;
  }
  const sig = map.events[i];
  return (
    sig.tick +
    (targetBar - map.barAt[i]) * ticksPerBar(sig, map.ppq) +
    (Math.max(1, Math.floor(beat)) - 1) * ticksPerBeat(sig, map.ppq) +
    Math.max(0, Math.floor(ticks))
  );
}

/** `12.3.240` style read-out used by the transport and timeline ruler. */
export function formatBarBeat(map: NormalisedSignatureMap, tick: number): string {
  const { bar, beat, ticks } = ticksToBarBeat(map, tick);
  return `${bar}.${beat}.${String(Math.floor(ticks)).padStart(3, '0')}`;
}

/** `1:23.456` style read-out. */
export function formatSeconds(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const rem = s - m * 60;
  return `${m}:${rem.toFixed(3).padStart(6, '0')}`;
}
